import { extractResponseBody } from '@lib/utils';
import lodashMap from 'lodash/map';
import { NzTreeNodeOptions } from 'ng-zorro-antd/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { filter, map, skip } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ServerApi } from '@app/services/server-api';
import { Session } from '@app/services/session';
import { EntGroupHierarchyConfigService } from '@app/services/dynamic-configs/ent-group-hierarchy-enabled';

interface IHierarchy {
  children?: this[];
}

export interface IResponseGroupWrapper {
  group_node: IResponseGroup;
}

export interface IResponseGroup {
  id: number,
  name: string,
  children: IResponseGroupWrapper[]
  total_assets?: number;
  total_assets_without_engine_hours?: number;
  aux?: {
    aux_input_1: {
      CONNECTED: number[];
    };
    aux_input_2: {
      CONNECTED: number[];
    };
  };
}

export interface ITransformGroupsWrapper {
  id: number,
  name: string,
  children: ITransformGroupsWrapper[]
  total_assets?: number;
  total_assets_without_engine_hours?: number;
  operating_eld_modes?: string;
  aux?: {
    aux_input_1: {
      CONNECTED: number[];
    };
    aux_input_2: {
      CONNECTED: number[];
    };
  }
}

export interface IGroupNode {
  title: string;
  key: string;
  isLeaf?: boolean;
}

export enum GROUP_MODAL_TYPE {
  CREATE_GROUP = 'createGroup',
  CREATE_SUBGROUP = 'createSubGroup',
  RENAME_GROUP = 'renameGroup',
  MOVE_GROUP = 'moveGroup',
}

export interface IGroupAction {
  type: GROUP_MODAL_TYPE;
  parentId?: string;
  group?: IGroupNode;
}

export interface IGroup {
  id: number;
  name: string;
  children: IGroup[];
  operatingEldModes: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminGroupService {
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  public groupModalActionEvents: Subject<IGroupAction> = new Subject();
  public selectedGroup: BehaviorSubject<string>;
  public disabledAllGroups$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly isGroupHierarchyEnabled: boolean;

  private cacheResponse$: BehaviorSubject<ITransformGroupsWrapper[]> = new BehaviorSubject([]);
  private treeNodesCache$: BehaviorSubject<NzTreeNodeOptions[]> = new BehaviorSubject(undefined);
  private treeNodesWithPtoCache$: BehaviorSubject<NzTreeNodeOptions[]> = new BehaviorSubject(undefined);
  private hierarchyCache$: BehaviorSubject<IGroup[]> = new BehaviorSubject(undefined);

  constructor(
    private session: Session,
    private message: NzMessageService,
    private api: ServerApi,
    private enterpriseGroupHierarchyConfigService: EntGroupHierarchyConfigService,
  ) {
    this.isGroupHierarchyEnabled = this.enterpriseGroupHierarchyConfigService.isGroupHierarchyEnabled();
    this.selectedGroup = new BehaviorSubject<string>('');
    this.refreshGroupsData();

    this.cacheResponse$
      .pipe(skip(1))
      .subscribe(nodes => {
        this.treeNodesCache$.next(this.transformTreeNodeOptions(nodes));
        this.hierarchyCache$.next(this.transformGroupHierarchyOptions(nodes));
      });
  }

  public get groups$(): Observable<NzTreeNodeOptions[]> {
    return this.treeNodesCache$.asObservable().pipe(
      filter(value => !!value),
    );
  }

  public get groupsWithPto$(): Observable<NzTreeNodeOptions[]> {
    const treeNodesWithPtoCache: NzTreeNodeOptions[] = this.treeNodesWithPtoCache$.value;
    if (!treeNodesWithPtoCache) {
      this.getAll(true)
        .subscribe(nodes => {
          const filterCheck: (node: ITransformGroupsWrapper) => boolean = (node: ITransformGroupsWrapper) =>
            (node.aux?.aux_input_1.CONNECTED.length || 0 + node.aux?.aux_input_2.CONNECTED.length || 0) > 0;
          const filteredNodes: ITransformGroupsWrapper[] = this.filterHierarchy(nodes, filterCheck);
          this.treeNodesWithPtoCache$.next(this.transformTreeNodeOptions(filteredNodes));
        });
    }
    return this.treeNodesWithPtoCache$.asObservable().pipe(filter(value => !!value));
  }

  public getGroupHierarchy(visibleGroups: number[]): Observable<IGroup[]> {
    return this.hierarchyCache$.asObservable()
      .pipe(
        filter(value => !!value),
        map(this.filterByVisibilityAndTransformGroups.bind(this, visibleGroups)),
      );
  }

  public getGroup(id: number): IGroup {
    return this.hierarchyCache$?.value?.reduce(function findGroup(acc: IGroup, group: IGroup) {
      if (!acc && group.id === id) acc = group;

      if (!acc && group.children.length > 0) {
        acc = group.children?.reduce((childAcc: IGroup, childGroup: IGroup) => findGroup(childAcc, childGroup), undefined);
      }

      return acc;
    }, undefined);
  }

  public refreshGroupsData(): void {
    this.isLoading$.next(true);
    this.getAll()
      .subscribe((nodes: ITransformGroupsWrapper[]) => {
        this.cacheResponse$.next(nodes);
        this.isLoading$.next(false);
      }, (errorResponse: HttpErrorResponse) => {
        let errorMsg: string = $localize`Failed to fetch groups, please try reloading the page.`;
        if (errorResponse.status === 404) errorMsg = errorResponse.statusText;
        this.message.error(errorMsg),
        this.isLoading$.next(false);
      });
  }

  private getAll(withPto: boolean = false): Observable<ITransformGroupsWrapper[]> {
    return this.api
    .get<IResponseGroupWrapper[]>(
      'groups/hierarchy',
      new Map()
        .set('include_asset_counts', true)
        .set('group_ids', null)
        .set('with_pto', withPto),
    )
    .pipe(
      map(extractResponseBody),
      map(this.transformResponse.bind(this)),
    );
  }

  private transformResponse(
    data: ITransformGroupsWrapper[] | IResponseGroupWrapper[],
    allDecendents: boolean = false,
  ): ITransformGroupsWrapper[] {
    return lodashMap(data, 'group_node').reduce((acc: ITransformGroupsWrapper[], group: ITransformGroupsWrapper) => {
      if (allDecendents || this.session.isInAllowedGroups(group.id)) {
        acc.push({
          id: group.id,
          name: group.name,
          operating_eld_modes: group.operating_eld_modes,
          total_assets: group.total_assets,
          total_assets_without_engine_hours: group.total_assets_without_engine_hours,
          children: this.isGroupHierarchyEnabled ? this.transformResponse(group.children, true) : [],
          aux: group.aux,
        });
      } else {
        if (!this.disabledAllGroups$.value) this.disabledAllGroups$.next(true);
        const nestedGroup = this.transformResponse(group.children, false);
        acc = acc.concat(nestedGroup);
      }

      return acc;
    }, []).sort((a: ITransformGroupsWrapper, b: ITransformGroupsWrapper) => a.name.localeCompare(b.name));
  }

  private transformTreeNodeOptions(
    data: ITransformGroupsWrapper[],
    level: number = 0,
  ): NzTreeNodeOptions[] {
    return data.map((group: ITransformGroupsWrapper) => {
      return {
        key: group.id.toString(),
        title: group.name,
        isLeaf: !this.isGroupHierarchyEnabled || group.children.length === 0,
        groupLevel: level,
        children: this.isGroupHierarchyEnabled ? this.transformTreeNodeOptions(group.children, level + 1) : null,
        totalAssets: group.total_assets,
        totalAssetsWithoutEngineHours: group.total_assets_without_engine_hours,
      };
    });
  }

  private transformGroupHierarchyOptions(data: ITransformGroupsWrapper[]): IGroup[] {
    return data.map((group: ITransformGroupsWrapper) => ({
      id: group.id,
      name: group.name,
      children: this.transformGroupHierarchyOptions(group.children || []),
      operatingEldModes: group.operating_eld_modes,
    }));
  }

  // TODO: Remove this and refactor the above function to seperate tree node from group node
  private filterByVisibilityAndTransformGroups(visibleGroups: number[], groups: IGroup[]): IGroup[] {
    return groups
      .reduce((acc: IGroup[], group: IGroup) => {
        if (!visibleGroups || !visibleGroups.length || visibleGroups?.includes(group.id)) {
          acc.push(group);
        } else {
          const upgradedGroups: IGroup[] = this.filterByVisibilityAndTransformGroups(visibleGroups, group.children);
          acc = acc.concat(upgradedGroups);
        }

        return acc;
      }, []);
  }

  private filterHierarchy<HierarchyType extends IHierarchy>(
    hierarchy: HierarchyType[],
    filterCheck: (item: HierarchyType) => boolean,
  ): HierarchyType[] {
    let newHierarchy: HierarchyType[] = [];
    hierarchy.forEach(parent => {
      const newChildren: HierarchyType[] = this.filterHierarchy(parent.children || [], filterCheck);
      if (filterCheck(parent)) {
        parent.children = newChildren;
        newHierarchy.push(parent);
      } else {
        newHierarchy = newHierarchy.concat(newChildren);
      }
    });
    return newHierarchy;
  }
}
